import React from "react";
import { Button } from "../../components/ui";
import { getFileUrl } from "../../core/config/api";
import {
  getBaseCurrency,
  resolveOriginalMoney,
  useCrossRate,
  useFormatMoneyWithBase,
  useGbpToBase,
} from "../../core/currency";
import { SECTION_IDS, getSectionStatus } from "./sectionConfig";
import OrderStatus from "../../components/order/Status";
import RequestCollectionSection from "./RequestCollectionSection";

const IconClient = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <circle cx="6" cy="4.5" r="2.2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M1.5 11c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);
const IconRequest = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <rect x="2" y="1.5" width="8" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4 4.5h4M4 6.5h4M4 8.5h2" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);
const IconQuote = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M2 3h8v5.5a.5.5 0 01-.5.5h-7a.5.5 0 01-.5-.5V3z" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4 1.5v1.5M8 1.5v1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M4 6h4M4 7.5h2" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);
const IconTradeIn = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M2 4h8M8.5 2l1.5 2-1.5 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 8H2M3.5 6L2 8l1.5 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconSummary = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M2 9.5h8M2 6.5h6M2 3.5h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);
const IconPayments = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <rect x="1.5" y="3" width="9" height="6.5" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M1.5 5.5h9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <rect x="3" y="7" width="2" height="1" rx="0.5" fill="currentColor" />
  </svg>
);
const IconInternal = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M2 9L4.5 6l2 2L9 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="9.5" cy="4" r="1" fill="currentColor" />
  </svg>
);
const IconInvoice = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M3 1.5h6v9l-1.5-.9-1.5.9-1.5-.9L3 10.5V1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M4.5 4.5h3M4.5 6.5h3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);
const IconProduct = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <rect x="1.5" y="2.5" width="9" height="7" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
    <circle cx="4" cy="5.5" r="0.8" fill="currentColor" />
    <path d="M1.5 8.5L3.5 6.5L5 8L6.5 6.5L10.5 9" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconDelivery = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M1.5 4h6v4.5H1.5z" stroke="currentColor" strokeWidth="1.2" />
    <path d="M7.5 5.5H10l1.5 2v1H7.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <circle cx="3.5" cy="9.5" r="1" stroke="currentColor" strokeWidth="1.1" />
    <circle cx="9" cy="9.5" r="1" stroke="currentColor" strokeWidth="1.1" />
  </svg>
);

// The listing's four handling-cost columns. They share ONE currency column
// (costs_currency) — see ListingService::updateGbpAmounts, which converts each
// of them with it.
const OTHER_COST_FIELDS = ["polishing", "accessories", "shipping", "travel"];

// A money record with no currency of its own is LEGACY GBP — the rule the
// backend's getGbpAmount() and resolveOriginalMoney() already follow. It must
// NEVER be read as "inherit the deal/quote currency": that is how order 87362's
// £23,500 GBP listing price came to be rendered "€23,500" in the SHOPPING CART.
// `listings.currency` DOES exist (char(7), migration 2026_07_21_150001) and is
// stamped by ListingService::resolveSellCurrency while MULTICURRENCY_ENABLED is
// on — so read it, and treat NULL as legacy GBP (which is what every row on
// production is today: the gbp_price backfill found 3,089 GBP and no others).
// Do NOT "add a currency column" — it is already there.
const LEGACY_CURRENCY = "gbp";
const ownCurrency = (record, field = "currency") =>
  String(record?.[field] || "").toLowerCase() || LEGACY_CURRENCY;

function Row({ label, value, tone, note, missingHint, onMissingClick }) {
  if (value == null || value === "" || value === "—") {
    if (!missingHint) return null;
    return (
      <div className={`rc-request-v2__inv-row rc-request-v2__inv-row--missing`}>
        <span className="rc-request-v2__inv-row-label">{label}</span>
        <Button
          variant="ghost"
          size="sm"
          data-btn="summary-open-missing"
          onClick={onMissingClick}
        >
          {missingHint}
        </Button>
      </div>
    );
  }
  return (
    <div
      className={`rc-request-v2__inv-row${tone ? ` rc-request-v2__inv-row--${tone}` : ""}`}
    >
      <span className="rc-request-v2__inv-row-label">{label}</span>
      <span className="rc-request-v2__inv-row-value">{value}</span>
      {note && <small className="rc-request-v2__inv-row-note">{note}</small>}
    </div>
  );
}

function SectionHeading({ title, icon, action }) {
  return (
    <div className="rc-request-v2__inv-heading">
      <span className="rc-request-v2__inv-heading-left">
        {icon && <span className="rc-request-v2__inv-heading-icon">{icon}</span>}
        <span>{title}</span>
      </span>
      {action && (
        <span className="rc-request-v2__inv-heading-action">{action}</span>
      )}
    </div>
  );
}

function InvAction({ children }) {
  return <div className="rc-request-v2__inv-actions">{children}</div>;
}

export function RequestSmartCard({ sections, state, onSelect }) {
  const goTo = onSelect;
  const findSection = (id) => sections.find((s) => s.id === id);

  // Internal P&L is stored in the GBP pivot (gbp_* columns / GBP-computed
  // commissions + net profit). Display it in the MERCHANT'S base currency —
  // each store has its own — via the shared GBP-pivot renderer. Unlike a raw
  // useFx rate (which falls back to 1 on failure, stamping the base symbol on an
  // unconverted GBP number), useGbpToBase labels the figure GBP when the rate is
  // unavailable. Byte-identical when base is gbp or multi-currency is off.
  const baseCcy = getBaseCurrency();
  const gbpBase = useGbpToBase();
  // GBP pivot → base, whole units, matching the card's decimals:0 style.
  const formatGbpPivot = (gbpPence) => gbpBase.format(gbpPence, { decimals: 0 });

  // Q9 bracket rule — every money field on this card whose currency differs
  // from the store base ALSO shows the base-converted value in brackets:
  // "$1,000 (HK$7,810)". Same currency → plain value. All card money renders
  // flow through this one formatter so every Row inherits the behaviour.
  const fmWithBase = useFormatMoneyWithBase();
  const formatMoney = (cents, currency = baseCcy) => {
    if (cents == null || cents === "" || isNaN(Number(cents))) return null;
    return fmWithBase(cents, currency ?? baseCcy, { decimals: 0 });
  };

  const productSection = findSection(SECTION_IDS.SEARCH_PRODUCT);
  // Drives the tick/dot on the Request Details heading. That block only
  // renders when the request carries a min/max budget, which is why the
  // missing declaration crashed on some requests and not others.
  const confirmSection = findSection(SECTION_IDS.CONFIRM_REQUEST);
  // A "jump there and fill it in" hint must not offer a step the flow will never
  // have. A variant DROPS sections outright — Record a Sale loses SELECT QUOTE
  // once a listing is the source — and selecting a missing id makes the shell's
  // stillVisible guard bounce the rep to the first section, so the control looks
  // live and quietly goes somewhere else.
  //
  // Keyed on the variant's own autoSkip, NOT on "is the section visible right
  // now": most steps are legitimately invisible early (SELECT QUOTE needs a
  // request first) and the hint is exactly how the card says "this is still to
  // come". Hiding those would empty the card out on arrival.
  const droppedByVariant = new Set(state.flowVariant?.autoSkip?.(state) ?? []);
  // A slot sourced from ACTIVE LISTINGS has no quote step at all — the listing
  // is the price — so the Quote row must not offer to jump to one. Same reason as
  // the dropped-by-variant case above: selecting a section that is not in the
  // flow makes the shell bounce the rep to the first section.
  const orderIsListingSourced = !!(
    findSection(SECTION_IDS.SEND_QUOTE) == null && state?.request?.linked_listing_id
  );
  const offersHint = (id) =>
    !droppedByVariant.has(id) &&
    !(id === SECTION_IDS.SEND_QUOTE && orderIsListingSourced);

  // Whole QUOTE block, not just its row: a deal sourced from ACTIVE LISTINGS has
  // no quote step and no quote, so the block rendered as a heading with nothing
  // under it. Its price is on this same card twice over — Shopping Cart (the
  // listing) and Payments (Balance Due) — so hiding it loses nothing.
  //
  // A DATABASE deal with a listing LINKED keeps the block: that is the Listings
  // linker (quotes.listing_id), the quote is the price, and the rep may still
  // change it. Same distinction that decides the SELECT ORDER QUOTE section.
  // Guarded on `quote` too, so a listing-sourced deal that somehow carries a
  // quote still shows it rather than hiding real money.
  // The heading should say what the rail and the accordion say: a variant renames
  // CONFIRM REQUEST to "Request Details". Read it off the variant rather than
  // confirmSection.label, which carries the "(1/3)" slot counter on multi-main
  // deals — a counter belongs on the section, not on this summary heading.
  const requestHeading =
    state.flowVariant?.sectionLabels?.[SECTION_IDS.CONFIRM_REQUEST] ?? "Request";

  const product = state.product || state.request?.product;
  // For multi-main deals: combine the primary product with any
  // additional main products into one ordered list so the right-side
  // Product card renders all 1-3 mains. Each entry carries its own
  // request so per-product year/condition/price annotate correctly.
  const mainProducts = [
    product
      ? {
          product,
          request: state.request,
          slotIdx: 0,
        }
      : null,
    ...(state.additionalMainProducts ?? []).map((p, i) => ({
      product: p,
      request: state.additionalMainRequests?.[i],
      slotIdx: i + 1,
    })),
  ].filter((e) => e && e.product);
  const client = state.client || state.request?.client;
  const request = state.request;
  const quote = request?.last_quote;
  // Declared HERE, after `quote` — reading it above is a temporal dead zone
  // throw that takes the whole card out, and no lint rule sees it.
  const hideQuoteBlock = orderIsListingSourced && !quote?.price;
  // The quote drives the price the client owes, so Balance Due / Total Paid
  // are denominated in the quote currency. Without this they fall back to
  // formatMoney's GBP default and mislabel a non-GBP deal (e.g. a $9,300 USD
  // quote shown as "Balance Due £9,300"). Internal costs stay GBP (they're
  // stored as gbp_*), so only the client-facing Payments rows use this.
  const linkedListing = (state.listings ?? []).find(
    (l) => l.id === quote?.listing_id
  );
  // DEAL currency — one shared resolution across the app:
  //   quote.currency → order.currency → listing.currency → store base.
  // The request's own currency is deliberately NOT in this chain (budgets
  // render in request.currency, but the deal is priced by quote/listing).
  // om.listingRate (threaded through enrichedState) is the provider's own
  // resolution of the same rule, used before falling to the base.
  // `deal_currency` (not `currency`) is the listing's quote-derived DEAL
  // currency, stamped by RequestTemplateV2 alongside the listing's own price
  // currency. `currency` is the currency of listing.price itself.
  const displayCurrency =
    quote?.currency
    ?? state.order?.currency
    ?? linkedListing?.deal_currency
    ?? (state.listings ?? [])[0]?.deal_currency
    ?? state.listingRate
    ?? getBaseCurrency();
  // The currency om's AGGREGATES (paidAmount, toPay, sellRequestsAmount) are
  // denominated in. om resolves the deal currency ORDER-first while
  // displayCurrency resolves it QUOTE-first, so on a legacy deal the two
  // legitimately differ — order 87362 has order.currency 'gbp' and a €26,850
  // quote. An om aggregate may therefore only ever be labelled with THIS.
  const omCurrency = state.listingRate ?? displayCurrency;
  // Per-LINE listing for a cart line (the primary main slot or one of the
  // additional main slots). A multi-item cart may legitimately price each
  // line in its own currency, so a line must never borrow the deal's — let
  // alone the request's budget — currency. Slot → listing resolves the same
  // way RequestTemplateV2 seeds state.listings: the slot's quote's listing,
  // then the Active-Listings path's linked_listing_id.
  const listingById = new Map(
    (state.listings ?? []).filter((l) => l?.id).map((l) => [l.id, l])
  );
  const listingForRequest = (r) => {
    const id = r?.last_quote?.listing_id ?? r?.linked_listing_id ?? null;
    if (id && listingById.has(id)) return listingById.get(id);
    // Single-main deal with a single seeded listing: the pointer may be
    // absent (listing linked before the quote existed) but the mapping is
    // unambiguous. Never guessed for multi-main — a wrong line is worse
    // than an unpriced one.
    if (mainProducts.length === 1 && (state.listings ?? []).length === 1) {
      return (state.listings ?? [])[0];
    }
    return null;
  };
  // Any-currency → any-currency, off ONE fetched FX table, so per-listing
  // original currencies can be converted without a hook per row.
  const crossRate = useCrossRate();
  // Sum {amount, currency} rows INTO `target`, each converted from ITS OWN
  // currency exactly once. Returns null when any leg's rate is unknown — an
  // understated total is worse than none (same rule as the Q9 base bracket).
  const sumMoneyInto = (rows, target) => {
    let total = 0;
    for (const row of rows) {
      const rate = crossRate(row.currency, target);
      if (rate == null) return null;
      total += Number(row.amount || 0) * rate;
    }
    return total;
  };
  // The single currency a set of rows agree on, else `fallback`.
  const soleCurrency = (rows, fallback) => {
    const set = new Set(rows.map((r) => ownCurrency(r)));
    return set.size === 1 ? [...set][0] : fallback;
  };
  const sellRequests = state.sellRequests ?? [];
  const selectedQuotes = state.quotes ?? {};
  const getSellQuote = (sr) => selectedQuotes[sr.id] ?? sr.last_quote ?? null;
  const order = state.order;
  const allDeposits = (order?.items ?? []).flatMap(
    (item) => item.deposits ?? []
  );
  const finalPayment = order?.payment;

  // Sell requests persist payments via PUT /api/requests/:id/add-price into
  // `request.deposits` (a JSON blob), which om never hydrates from. Fall
  // back to that blob so the BALANCE DUE row matches what the PAYMENTS
  // section calculates in-page (StepPayments treats stored amounts as
  // pounds and multiplies by 100 to compare to the pence-denominated
  // quote/listing price).
  const sellDeposits = request?.deposits?.deposits ?? [];
  const sellFinal = request?.deposits?.total_remaining_paid;
  const sellPaidPence =
    sellDeposits.reduce(
      (sum, d) => sum + (parseFloat(d?.amount) || 0),
      0
    ) * 100 +
    (parseFloat(sellFinal?.amount) || 0) * 100;
  const omPaid = typeof state.paidAmount === "number" ? state.paidAmount : 0;
  // Only feeds the GBP-pivot netProfit fallback below — never displayed.
  const totalPaid = omPaid > 0 ? omPaid : sellPaidPence;
  const toPay = typeof state.toPay === "number" ? state.toPay : null;

  // ── What the client has PAID, in the currency he actually paid ─────
  // INVARIANT (order 87362): amount and currency come off the SAME record and
  // nothing is converted twice. `state.paidAmount` cannot be used for display:
  // it is already converted into om's deal currency, so labelling it with the
  // quote's currency turned a 26,850 EUR payment into "€22,976" and the base
  // bracket then converted that converted figure AGAIN into "(£19,662)".
  // Prefer om's live rows (so the card tracks edits), then the persisted order
  // rows, then the sell-request blob.
  const persistedPaymentRows = [
    ...allDeposits,
    ...(finalPayment ? [finalPayment] : []),
  ]
    .filter((p) => Number(p?.amount) > 0)
    .map((p) => ({ amount: Number(p.amount), currency: ownCurrency(p) }));
  const sellPaymentRows = [...sellDeposits, ...(sellFinal ? [sellFinal] : [])]
    .filter((d) => (parseFloat(d?.amount) || 0) > 0)
    // The sell blob stores MAJOR units (StepPayments multiplies by 100).
    .map((d) => ({
      amount: (parseFloat(d.amount) || 0) * 100,
      currency: ownCurrency(d),
    }));
  const omPaymentRows = state.paymentRows ?? [];
  const paidRows = omPaymentRows.length
    ? omPaymentRows
    : persistedPaymentRows.length
      ? persistedPaymentRows
      : sellPaymentRows;
  const commissionsTotal = state.commissionsAmount ?? 0;
  // FX RULE (hooks/fx.js): om could not convert every amount, so its
  // commission / profit totals are withheld (null) — say so instead of
  // printing 0 or a figure built from them.
  const fxMissing = !!state.fxMissing;
  const fxLabel = state.fxUnavailable ? "Rate unavailable" : "…";
  // Revenue the client paid on top of the watch. Already inside the item profit
  // that commissionsTotal splits and that netProfitBeforeCommissions sums —
  // shown on its own row for visibility, never added to a total again.
  const expertFeeTotal = state.expertFeeGbp ?? 0;
  const expertFeeName = state.expertFeeName;
  const marketplaceFee = (Number(state.marketplaceFee ?? 0)) * 100;

  // ── What the client OWES ───────────────────────────────────────────
  // The accepted QUOTE is what the client was billed, so take its amount AND
  // its currency (one record); only a slot with no quote yet falls back to its
  // listing price in the LISTING's own currency. Deriving the balance from the
  // listing price instead compares a GBP figure with a EUR payment through
  // TODAY's rate, which can never come out at zero: order 87362's client paid
  // the €26,850 he was quoted in full, yet "23,500 − 22,976" surfaced a
  // phantom "Balance Due €524" for a rep to chase.
  const owedRows = mainProducts
    .map(({ request: slotRequest }) => {
      const slotQuote = slotRequest?.last_quote;
      if (slotQuote?.price != null && slotQuote.price !== "") {
        return {
          amount: Number(slotQuote.price),
          currency: ownCurrency(slotQuote),
        };
      }
      const slotListing = listingForRequest(slotRequest);
      if (slotListing?.price != null && slotListing.price !== "") {
        return {
          amount: Number(slotListing.price),
          currency: ownCurrency(slotListing),
        };
      }
      return null;
    })
    .filter(Boolean);
  const owedCurrency = soleCurrency(owedRows, displayCurrency);
  const owedTotal = sumMoneyInto(owedRows, owedCurrency);
  // Total Paid renders in the payments' own currency when they agree, else in
  // the currency the client owes.
  const paidCurrency = soleCurrency(paidRows, owedCurrency);
  const paidTotal = paidRows.length ? sumMoneyInto(paidRows, paidCurrency) : 0;

  // ── Cost / profit math ────────────────────────────────────────────
  // Two different jobs, deliberately kept apart:
  //  · buyingCosts / otherCosts stay in the GBP PIVOT — they only feed the
  //    netProfit formula, whose other terms (commissions) are GBP too.
  //  · the DISPLAYED cost rows use the ORIGINAL amount + currency the user
  //    typed (see sumOriginalMoney). Converting the pivot for display sent a
  //    typed 1000 USD through GBP 747 and back out as 993 USD.
  const buyingCosts = (state.listings ?? []).reduce(
    (acc, l) => acc + Number(l.gbp_buying_price ?? l.buying_price ?? 0),
    0
  );
  const listingsMissingBuyingPrice = (state.listings ?? []).filter(
    (l) => !Number(l.gbp_buying_price ?? l.buying_price ?? 0)
  );

  // Surface the "order has no listing linked yet" state on the Product
  // card. Without a listing, Payments stays locked — the Listings
  // section in the nav is where the rep attaches one. Tint fires from
  // the moment the order exists; the "No listing linked" call-to-action
  // also needs a quote to exist (since linking writes quote.listing_id),
  // so it stays gated on quote presence.
  // request_type covers plain "order" + PX variants ("px-order", "px-cart").
  // Some legacy code also uses isPxFlow on enrichedState — treat any
  // non-sell flow as order-side.
  const requestType = request?.request_type ?? null;
  const isSellReq = requestType === "sell";
  const isOrderRequest = !!requestType && !isSellReq;
  const orderQuoteId = request?.last_quote?.id ?? null;
  const orderListingId =
    request?.last_quote?.listing_id ?? request?.linked_listing_id ?? null;
  const orderMissingListing = isOrderRequest && !orderListingId;
  const canLinkListingNow = orderMissingListing && !!orderQuoteId;
  const otherCosts = (state.listings ?? []).reduce(
    (acc, l) =>
      acc +
      Number(l.gbp_polishing ?? 0) +
      Number(l.gbp_accessories ?? 0) +
      Number(l.gbp_shipping ?? 0) +
      Number(l.gbp_travel ?? 0),
    0
  );
  // Fallback P&L, used only when om isn't mounted to supply
  // netProfitBeforeCommissions. Every term must be GBP, because buyingCosts /
  // otherCosts / commissionsTotal are GBP pivots — but om's paidAmount and the
  // marketplace fee are denominated in om's DEAL currency, so each is converted
  // into GBP exactly once here. Subtracting them raw mixed currencies, which is
  // the same defect that produced order 87362's phantom €524 balance. When the
  // rate is unknown the figure is withheld rather than summed across
  // currencies (never display a wrong number).
  const omToGbp = crossRate(omCurrency, "gbp");
  const totalPaidGbp =
    omPaid > 0 ? (omToGbp == null ? null : omPaid * omToGbp) : sellPaidPence;
  const marketplaceFeeGbp =
    marketplaceFee === 0
      ? 0
      : omToGbp == null
        ? null
        : marketplaceFee * omToGbp;
  const netProfit =
    totalPaidGbp == null || marketplaceFeeGbp == null
      ? null
      : totalPaidGbp -
        buyingCosts -
        otherCosts -
        commissionsTotal -
        marketplaceFeeGbp;

  // Sum an original-money field across the deal's listings and return the
  // total WITH the currency it is denominated in. resolveOriginalMoney hands
  // back a self-consistent {amount, currency} pair — the original when the row
  // has one, the gbp_* pivot only for legacy rows with no currency stored.
  //
  // INVARIANT (order 87362): an internal cost is the STORE's money, not the
  // client's, so it renders in the currency it was entered in and is never
  // restated in the deal currency. Converting into the deal currency turned a
  // HK$210,000 buying price into "€23,313" — a figure that exists nowhere in
  // the data. Mixed currencies across lines have no single original, so those
  // fall back to the store BASE, where the other profit-side figures
  // (Commissions, Net Profit) already live. Returns null if ANY line's rate is
  // unknown: a silently understated total is worse than no total.
  const sumOriginalMoney = (fields, currencyField) => {
    const monies = (state.listings ?? [])
      .map((l) => resolveOriginalMoney(l, fields, currencyField))
      .filter(Boolean);
    if (!monies.length) return null;
    const currency = soleCurrency(monies, baseCcy);
    const amount = sumMoneyInto(monies, currency);
    return amount == null ? null : { amount, currency };
  };
  const buyingCostMoney = sumOriginalMoney("buying_price", "buying_currency");
  const otherCostsMoney = sumOriginalMoney(OTHER_COST_FIELDS, "costs_currency");
  // The Quote section's Buying Price — same rule, through the same helper, so
  // a legacy listing with no buying_currency falls back to its GBP pivot
  // (labelled GBP) instead of wearing the base symbol over a raw figure.
  const linkedBuying = resolveOriginalMoney(
    linkedListing,
    "buying_price",
    "buying_currency"
  );

  // ── Quote vs listing CURRENCY mismatch — informational, NEVER blocking ────
  // The backend enforces "quote currency AND value == listing currency AND
  // value" when a listing enters the cart (OrderValidationService), but only
  // behind MULTICURRENCY_ENABLED and only at cart time, so legacy deals were
  // never checked and nothing ever told the rep. Order 87362 carries a €26,850
  // quote against a £23,500 listing — a CURRENCY difference, which is what
  // makes its invoice, payment and balance figures disagree.
  //
  // Fires on the CURRENCY only. A value difference is normal and expected —
  // a quote is negotiated, so it almost always lands below the list price — so
  // testing the amount too put an amber notice on nearly every deal and trained
  // reps to ignore it. A currency difference is the structural fault: the
  // invoice, the payments and the balance are then denominated in different
  // units and cannot be reconciled at any rate.
  // Compare each record's OWN amount+currency and surface it inline.
  const quoteListingMismatch = (() => {
    if (isSellReq) return null;
    if (!quote?.price || !linkedListing?.price) return null;
    const quoteCcy = ownCurrency(quote);
    const listingCcy = ownCurrency(linkedListing);
    if (quoteCcy === listingCcy) {
      return null;
    }
    return {
      quoted: formatMoney(quote.price, quoteCcy),
      listed: formatMoney(linkedListing.price, listingCcy),
    };
  })();

  // The card's two leading blocks follow the FLOW's own order, so a variant that
  // starts with the client (Sourcing Enquiry, Sell Enquiry, Record a Sale) reads
  // CLIENT above PRODUCT here too. Derived from `sections` rather than from a
  // variant flag: however the flow ends up ordered, the summary agrees with it,
  // and the stock flows — product first — are untouched.
  const clientIdx = sections.findIndex((s) => s.id === SECTION_IDS.ASSIGN_CLIENT);
  const productIdx = sections.findIndex((s) => s.id === SECTION_IDS.SEARCH_PRODUCT);
  const clientFirst =
    clientIdx > -1 && (productIdx === -1 || clientIdx < productIdx);

  const productBlock = (() => {
          const productTitle = isSellReq
            ? "Sell Request"
            : isOrderRequest
              ? orderListingId
                ? "Shopping Cart"
                : "Order Request"
              : "Product";
          return (
        <section
          className={`rc-request-v2__inv-section${orderMissingListing ? " rc-request-v2__inv-section--needs-listing" : ""}`}
        >
          {request?.status && (
            <div className="rc-request-v2__inv-row rc-request-v2__inv-row--status">
              <span className="rc-request-v2__inv-row-label">Status</span>
              <OrderStatus status={request.status} />
            </div>
          )}
          <SectionHeading
            title={productTitle}
            icon={<IconProduct />}
            action={
              mainProducts.length === 0 && productSection ? (
                <Button
                  variant="ghost"
                  size="sm"
                  data-btn="summary-add-product"
                  onClick={() => goTo(SECTION_IDS.SEARCH_PRODUCT)}
                >
                  + Select product
                </Button>
              ) : null
            }
          />
          {mainProducts.length > 0 ? (
            <ul className="rc-request-v2__inv-lines">
              {mainProducts.map(({ product: p, request: r, slotIdx }) => {
                const thumbId = p?.images?.[0]?.id;
                // A cart line is priced by ITS OWN listing — amount AND
                // currency read off the same record, so they can never
                // disagree. Only when the slot has no listing yet does the
                // line fall back to the request's price expectation, which
                // legitimately IS denominated in the request's currency.
                const lineListing = listingForRequest(r);
                const lineListingPrice = lineListing?.price;
                return (
                  <li
                    key={`main-${slotIdx}-${r?.id ?? "primary"}`}
                    className="rc-request-v2__inv-line"
                  >
                    {thumbId && (
                      <img
                        className="rc-request-v2__inv-line-thumb"
                        src={getFileUrl(thumbId)}
                        alt=""
                      />
                    )}
                    <div className="rc-request-v2__inv-line-body">
                      <div className="rc-request-v2__inv-line-title">
                        {p?.brand?.name ?? "—"} {p?.model?.name ?? ""}
                      </div>
                      {p?.reference_number && (
                        <div className="rc-request-v2__inv-line-meta">
                          {p.reference_number}
                        </div>
                      )}
                      {r?.condition && (
                        <div className="rc-request-v2__inv-line-meta">
                          {Array.isArray(r.condition)
                            ? r.condition.join(", ")
                            : r.condition}
                        </div>
                      )}
                    </div>
                    {lineListingPrice != null && lineListingPrice !== "" ? (
                      <div className="rc-request-v2__inv-line-price">
                        {/* Listing price in the LISTING's OWN currency, with a
                            base bracket only when they differ. A listing with
                            no currency of its own is legacy GBP — it must never
                            fall back to the deal/quote currency, which is how
                            order 87362's £23,500 rendered as "€23,500". */}
                        {formatMoney(
                          lineListingPrice,
                          ownCurrency(lineListing)
                        )}
                      </div>
                    ) : r?.price_expectation ? (
                      <div className="rc-request-v2__inv-line-price">
                        {/* No listing on this line yet: the price expectation
                            belongs to the REQUEST, so it renders in the
                            request's own currency (amount and currency from
                            the same record) — never the deal currency. */}
                        {formatMoney(r.price_expectation, r?.currency ?? baseCcy)}
                      </div>
                    ) : (
                      r?.year && (
                        <div className="rc-request-v2__inv-line-price">
                          {r.year}
                        </div>
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}

          {canLinkListingNow && (
            <InvAction>
              <Button
                variant="secondary"
                size="sm"
                fullWidth
                align="start"
                data-btn="summary-open-listing"
                onClick={() => goTo(SECTION_IDS.CONNECT_LISTING)}
              >
                No listing linked — connect one to record payments
              </Button>
            </InvAction>
          )}
        </section>
          );
  })();

  const clientBlock = (
        <section className="rc-request-v2__inv-section">
          <SectionHeading title="Client" icon={<IconClient />} />
          <Row
            label={
              {
                client: "Client",
                merchant: "Merchant",
                expert: "Expert",
                concierge: "Concierge",
                personal_shopper: "Personal Shopper",
                supplier: "Supplier",
                watchmaker: "Watchmaker",
              }[client?.type] || "Client"
            }
            value={
              client?.id
                ? `${client?.full_name || [client?.first_name, client?.last_name].filter(Boolean).join(" ") || "—"}  ·  #${client.id}`
                : null
            }
            missingHint={
              offersHint(SECTION_IDS.ASSIGN_CLIENT) ? "Assign a client" : undefined
            }
            onMissingClick={() => goTo(SECTION_IDS.ASSIGN_CLIENT)}
          />
          {request?.channel && <Row label="Channel" value={request.channel} />}
        </section>
  );

  return (
    <aside className="rc-request-v2__card" aria-label="Snapshot">
      <div className="rc-request-v2__inv">
        <header className="rc-request-v2__inv-header">
          <div className="rc-request-v2__inv-title">Order Summary</div>
          {request?.id && (
            <div className="rc-request-v2__inv-ref">#{request.id}</div>
          )}
        </header>

        {/* ── Product + Client, in the order this flow uses ─────── */}
        {clientFirst ? (
          <>
            {clientBlock}
            {productBlock}
          </>
        ) : (
          <>
            {productBlock}
            {clientBlock}
          </>
        )}

        {/* ── Request Details ──────────────────────────────────── */}
        {(request?.min_budget || request?.max_budget) && (
          <section className="rc-request-v2__inv-section">
            <SectionHeading
              title={requestHeading}
              icon={<IconRequest />}
              // NOTE: SectionHeading takes ({ title, icon, action }) only — these
              // two have never been read. Left as they were rather than wired up:
              // making them work would add a status dot and a click target to this
              // heading that nobody has asked for. Flagged, not silently changed.
              status={
                confirmSection ? getSectionStatus(confirmSection, state) : null
              }
              onGoTo={() => goTo(SECTION_IDS.CONFIRM_REQUEST)}
            />
            {/* Budgets always render in the REQUEST's currency (falling back
                to the store base) — never the deal currency. */}
            <Row
              label="Min Budget"
              value={formatMoney(request.min_budget, request.currency ?? baseCcy)}
            />
            <Row
              label="Max Budget"
              value={formatMoney(request.max_budget, request.currency ?? baseCcy)}
            />
            {request.status && (
              <div className="rc-request-v2__inv-row">
                <span className="rc-request-v2__inv-row-label">Status</span>
                <OrderStatus status={request.status} />
              </div>
            )}
          </section>
        )}

        {/* ── Quote (hidden for an Active-Listings deal — see above) ──── */}
        {!hideQuoteBlock && (
        <section className="rc-request-v2__inv-section">
          <SectionHeading title="Quote" icon={<IconQuote />} />
          <Row
            label="Quoted Price"
            value={formatMoney(quote?.price, quote?.currency)}
            missingHint={
              offersHint(SECTION_IDS.SEND_QUOTE) ? "Send a quote" : undefined
            }
            onMissingClick={() => goTo(SECTION_IDS.SEND_QUOTE)}
          />
          {state.requestType === "sell" ? (
            <Row
              label="Future Selling Price"
              value={
                quote?.trade_avg != null && quote?.market_avg != null
                  ? // trade_avg/market_avg are GBP magnitudes (seeded from
                    // *.month.gbp). They are MARKET data — the store's own view
                    // of resale value, not the client's money — so like every
                    // other GBP pivot they render in the store base with ONE
                    // conversion. Converting them into the QUOTE's currency and
                    // then letting the Q9 bracket convert that figure into the
                    // base was a SECOND conversion off an already-converted
                    // number, so the bracket no longer matched the stored GBP.
                    formatGbpPivot(
                      Math.round(
                        (Number(quote.trade_avg) + Number(quote.market_avg)) / 2
                      )
                    )
                  : null
              }
            />
          ) : (
            <>
              <Row
                label="Buying Price"
                value={
                  linkedBuying
                    ? formatMoney(linkedBuying.amount, linkedBuying.currency)
                    : null
                }
              />
              <Row label="Supplier" value={linkedListing?.supplier ?? null} />
            </>
          )}
          {/* Informational only — saving, quoting and invoicing all stay
              available. Order 87362: quote €26,850 vs listing £23,500. */}
          {quoteListingMismatch && (
            <div className="rc-request-v2__inv-warn" role="status">
              Quoted {quoteListingMismatch.quoted} is in a different currency
              from the listing price {quoteListingMismatch.listed}. Align them
              before invoicing, or the invoice, payments and balance will
              disagree.
            </div>
          )}
        </section>
        )}

        {/* ── Trade-ins (PX) ───────────────────────────────────── */}
        {state.isPxFlow && (
          <section className="rc-request-v2__inv-section">
            <SectionHeading
              title="Trade-ins"
              icon={<IconTradeIn />}
              action={
                sellRequests.length === 0 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-btn="summary-add-tradein"
                    onClick={() => goTo(SECTION_IDS.PX_SUMMARY)}
                  >
                    + Add trade-in items
                  </Button>
                ) : null
              }
            />
            {sellRequests.length === 0 ? null : (
              <ul className="rc-request-v2__inv-lines">
                {sellRequests.map((sr) => {
                  const thumbId = sr.product?.images?.[0]?.id;
                  const sellQuote = getSellQuote(sr);
                  return (
                    <li key={sr.id} className="rc-request-v2__inv-line">
                      {thumbId && (
                        <img
                          className="rc-request-v2__inv-line-thumb"
                          src={getFileUrl(thumbId)}
                          alt=""
                        />
                      )}
                      <div className="rc-request-v2__inv-line-body">
                        <div className="rc-request-v2__inv-line-title">
                          {sr.product?.brand?.name ?? "—"}{" "}
                          {sr.product?.model?.name ?? ""}
                        </div>
                        <div className="rc-request-v2__inv-line-meta">
                          {sr.product?.reference_number ?? "—"}
                          {sr.status ? ` · ${sr.status}` : ""}
                        </div>
                      </div>
                      <div className="rc-request-v2__inv-line-price">
                        {/* Selected quote price in ITS OWN currency; before a
                            quote exists fall back to the sell request's price
                            expectation in the request's currency. */}
                        {(sellQuote?.price != null
                          ? formatMoney(sellQuote.price, sellQuote.currency)
                          : formatMoney(
                              sr.price_expectation,
                              sr.currency ?? baseCcy
                            )) ?? "—"}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {/* ── PX Summary totals ────────────────────────────────── */}
        {state.isPxFlow && (
          <section className="rc-request-v2__inv-section rc-request-v2__inv-section--totals">
            <SectionHeading title="PX Summary" icon={<IconSummary />} />
            {(() => {
              // Sell Quotes Total: prefer om.sellRequestsAmount (threaded via
              // enrichedState) — every quote already converted INTO the deal
              // currency. Only fall back to the raw same-currency sum when the
              // provider isn't mounted (identical for a single-currency deal).
              const sellTotal =
                typeof state.sellRequestsAmount === "number" &&
                state.sellRequestsAmount > 0
                  ? state.sellRequestsAmount
                  : // Fallback (provider not mounted): each selected quote
                    // converted ONCE from ITS OWN currency into omCurrency. The
                    // raw reduce this replaces added quote prices across
                    // currencies and then labelled the sum with omCurrency.
                    sumMoneyInto(
                      sellRequests
                        .map(getSellQuote)
                        .filter((q) => q?.price != null && q.price !== "")
                        .map((q) => ({
                          amount: Number(q.price),
                          currency: ownCurrency(q),
                        })),
                      omCurrency
                    );
              // Every figure in this block is an om aggregate (sellTotal, toPay)
              // already converted into om's deal currency, so the whole block is
              // labelled with THAT (omCurrency) — never the quote's currency,
              // which om may have resolved differently (order 87362).
              // Each listing price is converted from ITS OWN currency (legacy
              // GBP when it has none) rather than assumed to be deal-currency.
              const listingRows = (state.listings ?? [])
                .filter((l) => l?.price != null && l.price !== "")
                .map((l) => ({
                  amount: Number(l.price),
                  currency: ownCurrency(l),
                }));
              const listingTotal = sumMoneyInto(listingRows, omCurrency);
              const net =
                typeof toPay === "number"
                  ? toPay
                  : listingTotal == null || sellTotal == null
                    ? null
                    : listingTotal - sellTotal;
              return (
                <>
                  <Row
                    label="Listing Price"
                    value={formatMoney(listingTotal, omCurrency)}
                  />
                  <Row
                    label="Sell Quotes Total"
                    value={sellTotal ? `−${formatMoney(sellTotal, omCurrency)}` : null}
                  />
                  <Row
                    label={net >= 0 ? "Client to Pay" : "Store to Pay"}
                    value={
                      net == null ? null : formatMoney(Math.abs(net), omCurrency)
                    }
                    tone={net >= 0 ? "neutral" : "negative"}
                  />
                </>
              );
            })()}
          </section>
        )}

        {/* ── Payments ─────────────────────────────────────────── */}
        <section className="rc-request-v2__inv-section">
          <SectionHeading
            title="Payments"
            icon={<IconPayments />}
            action={
              allDeposits.length === 0 && !finalPayment ? (
                <Button
                  variant="ghost"
                  size="sm"
                  data-btn="summary-add-payment"
                  onClick={() => goTo(SECTION_IDS.PAYMENTS)}
                >
                  + Record payments
                </Button>
              ) : null
            }
          />
          {/* Each row in the currency it was taken in (null ⇒ legacy GBP). */}
          {allDeposits.map((d, i) => (
            <Row
              key={d.id ?? i}
              label={`Deposit ${i + 1}`}
              value={formatMoney(d.amount, ownCurrency(d))}
            />
          ))}
          {finalPayment?.amount > 0 && (
            <Row
              label="Final Payment"
              value={formatMoney(finalPayment.amount, ownCurrency(finalPayment))}
            />
          )}
          {paidTotal > 0 && (
            <Row
              label="Total Paid"
              // Summed from the payment rows' OWN currencies, so the label is
              // the currency the client actually paid in and the base bracket
              // converts the ORIGINAL exactly once (order 87362: "€26,850
              // (£22,976)", not "€22,976 (£19,662)").
              value={formatMoney(paidTotal, paidCurrency)}
              tone="positive"
            />
          )}
          {(() => {
            // Balance = owed − paid, both expressed in the currency the client
            // was BILLED (the quote's), each converted once from its own
            // record's currency. Sell flow owes the SELLER quote.price; the PX
            // `toPay = listingsPrice − sellRequestsPrice` formula produces
            // nonsense balances when applied to a pure sell (see sell request
            // 85975 where listing £13,500 − quote £12,700 surfaced as Balance
            // Due £800), and om's toPay/sellRequestsAmount are denominated in
            // om's own deal currency (omCurrency), not the quote's.
            const target = isSellReq
              ? quote?.price != null
                ? { amount: Number(quote.price), currency: ownCurrency(quote) }
                : null
              : state.isPxFlow || owedRows.length === 0 || owedTotal == null
                ? toPay == null
                  ? null
                  : { amount: toPay, currency: omCurrency }
                : { amount: owedTotal, currency: owedCurrency };
            if (!target) return null;
            const paidInTarget = paidRows.length
              ? sumMoneyInto(paidRows, target.currency)
              : 0;
            if (paidInTarget == null) return null;
            // A marketplace fee is retained by the marketplace, so the client
            // remits the price MINUS the fee — om models that by folding the
            // fee into paidAmount. paidRows are real client payments only, so
            // net the fee off what's owed instead (same result, no fake
            // payment). The fee is denominated in om's deal currency.
            const feeInTarget = marketplaceFee
              ? marketplaceFee * (crossRate(omCurrency, target.currency) ?? NaN)
              : 0;
            if (!isFinite(feeInTarget)) return null;
            const delta = target.amount - feeInTarget - paidInTarget;
            return (
              <Row
                label={delta >= 0 ? "Balance Due" : "Overpaid"}
                value={formatMoney(Math.abs(delta), target.currency)}
                tone={delta >= 0 ? "neutral" : "negative"}
              />
            );
          })()}
        </section>

        {/* ── Invoice ──────────────────────────────────────────── */}
        {isOrderRequest && (
          <section className="rc-request-v2__inv-section">
            <SectionHeading title="Invoice" icon={<IconInvoice />} />
            <Row
              label="Invoice No."
              value={order?.xero_invoice_number ?? order?.invoice_number ?? null}
              missingHint="Generate invoice"
              onMissingClick={() => goTo(SECTION_IDS.INVOICE)}
            />
            {(order?.xero_invoice_number || order?.invoice_number || state.getPdf) && (
              <Row
                label="PDF"
                value={state.getPdf ? "Generated" : null}
              />
            )}
          </section>
        )}

        {/* ── Adjustments (costs / profit) ─────────────────────── */}
        {isOrderRequest && (
          <section className="rc-request-v2__inv-section">
            <SectionHeading title="Adjustments" icon={<IconInternal />} />
            {listingsMissingBuyingPrice.length > 0 && (
              <InvAction>
                <Button
                  variant="secondary"
                  size="sm"
                  fullWidth
                  align="start"
                  data-btn="summary-open-costs"
                  onClick={() => goTo(SECTION_IDS.INTERNAL_DETAILS)}
                >
                  Add buying price in Internal Details & Costs
                </Button>
              </InvAction>
            )}
            {/* Buying/Other costs render the ORIGINAL amount the user typed
                (buying_price + buying_currency, and the four handling columns
                + costs_currency) IN THAT CURRENCY — never round-tripped through
                the gbp_* pivot and never restated in the deal currency, which
                showed order 87362's HK$210,000 buying price as "€23,313".
                The Q9 base bracket comes from the shared formatter. */}
            <Row
              label="Buying Cost"
              value={
                buyingCostMoney
                  ? formatMoney(buyingCostMoney.amount, buyingCostMoney.currency)
                  : null
              }
            />
            <Row
              label="Other Costs"
              value={
                otherCostsMoney
                  ? formatMoney(otherCostsMoney.amount, otherCostsMoney.currency)
                  : null
              }
            />
            {/* Commissions are computed in GBP by om — like Net Profit they
                are profit-side internals, shown in the store BASE currency.
                The order's EXPERT FEE is included in this figure (om folds it
                into commissionsAmount), so when there is one the note says how
                much of the total it is — otherwise a fee to an outside expert
                would be indistinguishable from a bigger sales commission. */}
            {/* The order's EXPERT FEE — revenue the client paid on top of the
                watch, so it RAISES the profit the commissions below are taken
                from. Its own row so the merchant can see the amount and which
                expert it relates to, with a note that it is already counted in
                the profit rather than being a separate line to add or subtract.
                Rendered only when the record is complete (amount + expert): the
                zero case would print "£0" and imply a fee was considered. */}
            {!!expertFeeTotal && (
              <Row
                label="Expert Fee"
                value={`+${formatGbpPivot(expertFeeTotal)}`}
                note={
                  expertFeeName
                    ? `${expertFeeName} · added to profit before commissions`
                    : "added to profit before commissions"
                }
                tone="neutral"
              />
            )}
            <Row
              label="Commissions"
              value={fxMissing ? fxLabel : formatGbpPivot(commissionsTotal)}
              note={
                expertFeeTotal
                  ? "share of profit incl. the expert fee · subtracted from profit"
                  : "subtracted from profit"
              }
            />
            {/* Marketplace fee is entered as a bare number and om adds it into
                paidAmount WITHOUT conversion, so it is denominated in om's deal
                currency — label it with that, not the quote's. */}
            <Row
              label="Marketplace Fee"
              value={formatMoney(marketplaceFee, omCurrency)}
            />
            {fxMissing && <Row label="Net Profit" value={fxLabel} />}
            {!fxMissing && (() => {
              const npBefore =
                typeof state.netProfitBeforeCommissions === "number"
                  ? state.netProfitBeforeCommissions
                  : null;
              if (npBefore == null && totalPaid <= 0) return null;
              // Both terms are GBP pivots. `netProfit` is null when its
              // deal-currency → GBP rate is unavailable, in which case there is
              // no profit figure to show at all.
              const beforeCommissions = npBefore ?? netProfit;
              if (beforeCommissions == null) return null;
              const after = beforeCommissions - commissionsTotal;
              return (
                <>
                  {npBefore != null && (
                    <Row
                      label="Net Profit Before Commissions"
                      value={`${npBefore >= 0 ? "+" : "−"}${formatGbpPivot(
                        Math.abs(npBefore)
                      )}`}
                      tone="neutral"
                    />
                  )}
                  <Row
                    label="Net Profit"
                    value={`${after >= 0 ? "+" : "−"}${formatGbpPivot(
                      Math.abs(after)
                    )}`}
                    tone={after >= 0 ? "positive" : "negative"}
                  />
                </>
              );
            })()}
          </section>
        )}

        {/* ── Collection / courier (Dashboard v2) ──────────────── */}
        <RequestCollectionSection request={request} user={state?.user} />

        {/* ── Tracking ─────────────────────────────────────────── */}
        {order?.tracking_number && (
          <section className="rc-request-v2__inv-section">
            <SectionHeading title="Delivery" icon={<IconDelivery />} />
            <Row label="Tracking" value={order.tracking_number} />
          </section>
        )}
      </div>
    </aside>
  );
}
